import { interfaceError } from '../../i18n/errorMessages';
import toast from '../../i18n/toast';
import React, {useEffect, useState} from "react";
import {connect} from "react-redux";
import * as PropTypes from "prop-types";
import { useLocation } from "react-router-dom";
import { filterArray } from '../common/Helpers';
import ContactDetailsFilter from './ContactDetailsFilter';
import { matchesContactFilter, normalizeContactFilter } from './contactFilterRules';
import { history } from "../../redux/store/configureStore";
import TeachersList from "./TeachersList";
import { getEffectivePermissionRole } from "./rbac";
import * as teacherActions from "../../redux/actions/teacherActions";
import {
  buildManageRouteWithSchoolYear,
  getNormalizedSchoolYearStartYearFromSearch,
} from "./manageSchoolYearRouting";
import { useTranslation } from 'react-i18next';

const isActiveTeacher = teacher => teacher.archived !== true && teacher.deleted !== true;
const isArchivedTeacher = teacher => teacher.archived === true && teacher.deleted !== true;

export function Teachers({
  teachers,
  loadTeachers,
  peopleViewState,
  onPeopleViewStateChange,
  readOnly,
}) {
  const { t: tr } = useTranslation();
  const [contactFilter, setContactFilter] = useState(normalizeContactFilter(peopleViewState && peopleViewState.contactFilter));
  const location = useLocation();
  const activeSchoolYearStartYear = getNormalizedSchoolYearStartYearFromSearch(location.search);
  const [search, setSearch] = useState(peopleViewState.search || "");
  const [showArchivedTeachers, setShowArchivedTeachers] = useState(Boolean(peopleViewState.showArchived));

  useEffect(() => {
    if (teachers.length === 0) {
      loadTeachers()
        .catch((error) => {
          toast.error(interfaceError(error, 'common:errors.load'));
        });
    }
  }, []);


  const handleEdit = (teacher) => {
    history.push(buildManageRouteWithSchoolYear(`/manage/teacher/${teacher.id}`, location.search, activeSchoolYearStartYear));
  };

  const handleAdd = () => {
    history.push(buildManageRouteWithSchoolYear("/manage/teacher/new", location.search, activeSchoolYearStartYear));
  };

  const handleShowArchivedTeachers = () => {
    const nextValue = !showArchivedTeachers;
    setShowArchivedTeachers(nextValue);
    onPeopleViewStateChange({ showArchived: nextValue });
  };

  const archivedTeachers = showArchivedTeachers
    ? teachers.filter(isArchivedTeacher)
    : teachers.filter(isActiveTeacher);

  const filteredTeachers = filterArray(search, archivedTeachers).filter(person => matchesContactFilter(person, contactFilter));

  const handleContactFilter = value => {
    setContactFilter(value);
    onPeopleViewStateChange({ contactFilter: value });
  };
  const clearFilters = () => {
    setSearch('');
    setContactFilter('all');
    setShowArchivedTeachers(false);
    onPeopleViewStateChange({ search: '', contactFilter: 'all', showArchived: false });
  };

  return (
    <div className="mb-5">
      <h2 className="manage-title mb-3 pb-0">{tr("common:teachers")}</h2>
      <div className="contact-toolbar">
      <input aria-label={tr("manage:searchTeachers_358")} placeholder={tr("manage:search_153")} className="form-control contact-search" value={search} onChange={event => {
        setSearch(event.target.value);
        onPeopleViewStateChange({ search: event.target.value });
      }} />
      <ContactDetailsFilter value={contactFilter} onChange={handleContactFilter} onClear={clearFilters} empty={filteredTeachers.length === 0} />
        <label className="contact-archive"><input type="checkbox" checked={showArchivedTeachers} onChange={handleShowArchivedTeachers} />{tr("manage:archive_154")}</label>
        {!readOnly && !showArchivedTeachers && <button type="button" className="btn manage-button btn-success contact-add" onClick={handleAdd}>{tr("manage:addANewTeacher_359")}</button>}
      </div>
      <TeachersList hideControls
        teachers={filteredTeachers}
        onAdd={handleAdd}
        onEdit={handleEdit}
        showArchivedTeachers={showArchivedTeachers}
        onShowArchivedTeachers={handleShowArchivedTeachers}
        initialNumToShow={peopleViewState.numToShow}
        onNumToShowChange={numToShow => onPeopleViewStateChange({ numToShow })}
      />
    </div>
  );
}

Teachers.propTypes = {
  readOnly: PropTypes.bool,
  teachers: PropTypes.array.isRequired,
  loadTeachers: PropTypes.func.isRequired,
  peopleViewState: PropTypes.shape({
    showArchived: PropTypes.bool,
    numToShow: PropTypes.number,
    contactFilter: PropTypes.string,
    search: PropTypes.string,
  }),
  onPeopleViewStateChange: PropTypes.func,
};

Teachers.defaultProps = {
  readOnly: false,
  peopleViewState: { showArchived: false, numToShow: 25 },
  onPeopleViewStateChange: () => {},
};

const mapStateToProps = state => ({
  readOnly: getEffectivePermissionRole(state.user) === "TEACHER",
  teachers: state.teachers,
});

const mapDispatchToProps = dispatch => ({
  loadTeachers: () => dispatch(teacherActions.loadTeachers()),
});

export default connect(mapStateToProps, mapDispatchToProps)(Teachers);
